import React, { useState, useEffect, useCallback } from 'react';
import Dashboard from './Dashboard';
import Proposals from './Proposals';
import Partners from './Partners';
import FeaturesTasks from './FeaturesTasks';
import Documents from './Documents';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupIcon from '@mui/icons-material/Group';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FolderIcon from '@mui/icons-material/Folder';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Badge from '@mui/material/Badge';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import SearchIcon from '@mui/icons-material/Search';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import SpeedDialIcon from '@mui/icons-material/Add';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import SaveIcon from '@mui/icons-material/Save';
import PrintIcon from '@mui/icons-material/Print';
import ShareIcon from '@mui/icons-material/Share';
import EditIcon from '@mui/icons-material/Edit';

const drawerWidth = 280;

const navItems = [
  { key: 'dashboard', label: 'Dashboard', icon: <DashboardIcon />, badge: null, description: 'Overview and analytics' },
  { key: 'proposals', label: 'Proposals', icon: <DescriptionIcon />, badge: 12, description: 'Manage project proposals' },
  { key: 'partners', label: 'Partners', icon: <GroupIcon />, badge: 3, description: 'Partner management' },
  { key: 'featuresTasks', label: 'Features & Tasks', icon: <AssignmentIcon />, badge: 8, description: 'Feature and task tracking' },
  { key: 'documents', label: 'Documents', icon: <FolderIcon />, badge: null, description: 'Document management' },
];

const speedDialActions = [
  { icon: <FileCopyIcon />, name: 'Copy', action: 'copy' },
  { icon: <SaveIcon />, name: 'Save', action: 'save' },
  { icon: <PrintIcon />, name: 'Print', action: 'print' },
  { icon: <ShareIcon />, name: 'Share', action: 'share' },
  { icon: <EditIcon />, name: 'Edit', action: 'edit' },
];

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { 
      main: '#2563eb',
      light: '#3b82f6',
      dark: '#1d4ed8'
    },
    secondary: { 
      main: '#7c3aed',
      light: '#8b5cf6',
      dark: '#6d28d9'
    },
    background: { 
      default: '#f8fafc',
      paper: '#ffffff'
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b'
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669'
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706'
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626'
    }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.3
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.4
    },
    body1: {
      fontSize: '0.875rem',
      lineHeight: 1.6
    },
    body2: {
      fontSize: '0.75rem',
      lineHeight: 1.5
    }
  },
  shape: {
    borderRadius: 12
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.05)',
    '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
    '0px 4px 6px rgba(0, 0, 0, 0.1), 0px 2px 4px rgba(0, 0, 0, 0.06)',
    '0px 10px 15px rgba(0, 0, 0, 0.1), 0px 4px 6px rgba(0, 0, 0, 0.05)',
    '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
    '0px 25px 50px rgba(0, 0, 0, 0.1)',
    '0px 25px 50px rgba(0, 0, 0, 0.1)',
    '0px 25px 50px rgba(0, 0, 0, 0.1)',
    '0px 25px 50px rgba(0, 0, 0, 0.1)',
    '0px 25px 50px rgba(0, 0, 0, 0.1)',
    '0px 25px 50px rgba(0, 0, 0, 0.1)',
    '0px 25px 50px rgba(0, 0, 0, 0.1)',
    '0px 25px 50px rgba(0, 0, 0, 0.1)',
    '0px 25px 50px rgba(0, 0, 0, 0.1)',
    '0px 25px 50px rgba(0, 0, 0, 0.1)',
    '0px 25px 50px rgba(0, 0, 0, 0.1)',
    '0px 25px 50px rgba(0, 0, 0, 0.1)',
    '0px 25px 50px rgba(0, 0, 0, 0.1)',
    '0px 25px 50px rgba(0, 0, 0, 0.1)',
    '0px 25px 50px rgba(0, 0, 0, 0.1)',
    '0px 25px 50px rgba(0, 0, 0, 0.1)',
    '0px 25px 50px rgba(0, 0, 0, 0.1)',
    '0px 25px 50px rgba(0, 0, 0, 0.1)',
    '0px 25px 50px rgba(0, 0, 0, 0.1)'
  ]
});

function App() {
  const [page, setPage] = useState('dashboard');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationSeverity, setNotificationSeverity] = useState('success');
  const [onlineStatus, setOnlineStatus] = useState(navigator.onLine);
  const [lastActivity, setLastActivity] = useState(new Date());
  const [userProfile, setUserProfile] = useState({
    name: 'Admin User',
    email: 'admin@example.com',
    avatar: 'A',
    role: 'Administrator',
    lastLogin: new Date().toLocaleString()
  });

  // Simulate real-time notifications
  useEffect(() => {
    const interval = setInterval(() => {
      const newNotification = {
        id: Date.now(),
        title: `Update ${Math.floor(Math.random() * 100)}`,
        message: 'New data available',
        time: new Date().toLocaleTimeString(),
        type: ['info', 'warning', 'success', 'error'][Math.floor(Math.random() * 4)]
      };
      setNotifications(prev => [newNotification, ...prev.slice(0, 4)]);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  // Online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setOnlineStatus(true);
      setNotificationMessage('You are back online!');
      setNotificationSeverity('success');
      setShowNotification(true);
    };

    const handleOffline = () => {
      setOnlineStatus(false);
      setNotificationMessage('You are offline. Some features may be limited.');
      setNotificationSeverity('warning');
      setShowNotification(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Activity tracking
  useEffect(() => {
    const updateActivity = () => setLastActivity(new Date());
    
    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keypress', updateActivity);
    window.addEventListener('click', updateActivity);
    window.addEventListener('scroll', updateActivity);

    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keypress', updateActivity);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('scroll', updateActivity);
    };
  }, []);

  // Handle navigation
  const handleNavigation = useCallback((newPage) => {
    setPage(newPage);
    setMobileOpen(false);
    setNotificationMessage(`Navigated to ${navItems.find(item => item.key === newPage)?.label}`);
    setNotificationSeverity('info');
    setShowNotification(true);
  }, []);

  // Handle search
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (query.trim()) {
      setNotificationMessage(`Searching for: ${query}`);
      setNotificationSeverity('info');
      setShowNotification(true);
    }
  }, []);

  // Handle theme toggle
  const handleThemeToggle = useCallback(() => {
    setDarkMode(!darkMode);
    setNotificationMessage(`Switched to ${!darkMode ? 'dark' : 'light'} mode`);
    setNotificationSeverity('info');
    setShowNotification(true);
  }, [darkMode]);

  // Handle speed dial actions
  const handleSpeedDialAction = useCallback((action) => {
    setNotificationMessage(`${action} action triggered`);
    setNotificationSeverity('success');
    setShowNotification(true);
  }, []);

  // Handle logout
  const handleLogout = useCallback(() => {
    setNotificationMessage('Logging out...');
    setNotificationSeverity('info');
    setShowNotification(true);
    setTimeout(() => {
      // Simulate logout
      setUserMenuAnchor(null);
    }, 1000);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNotificationClick = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleUserMenuClick = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleCloseNotification = () => {
    setNotificationAnchor(null);
  };

  const handleCloseUserMenu = () => {
    setUserMenuAnchor(null);
  };

  const currentPage = navItems.find(item => item.key === page);
  
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Sidebar */}
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              background: 'linear-gradient(180deg, #1e293b 0%, #334155 100%)',
              border: 'none',
              boxShadow: '4px 0 20px rgba(0, 0, 0, 0.1)'
            },
          }}
        >
          {/* Logo Section */}
          <Box sx={{ 
            p: 3, 
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            background: 'rgba(255, 255, 255, 0.05)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ 
                bgcolor: 'primary.main',
                width: 40,
                height: 40,
                fontSize: '1.2rem',
                fontWeight: 'bold'
              }}>
                ET
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ 
                  color: 'white', 
                  fontWeight: 700,
                  fontSize: '1.1rem'
                }}>
                  Estimation Tool
                </Typography>
                <Typography variant="caption" sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.75rem'
                }}>
                  Admin Platform
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Navigation */}
          <Box sx={{ flexGrow: 1, pt: 2 }}>
            <List>
              {navItems.map((item) => (
                <ListItem key={item.key} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    selected={page === item.key}
                    onClick={() => handleNavigation(item.key)}
                    sx={{
                      mx: 1,
                      borderRadius: 2,
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        },
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ 
                      color: page === item.key ? 'white' : 'rgba(255, 255, 255, 0.7)',
                      minWidth: 40
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.label} 
                      secondary={item.description}
                      sx={{
                        '& .MuiListItemText-primary': {
                          color: page === item.key ? 'white' : 'rgba(255, 255, 255, 0.8)',
                          fontWeight: page === item.key ? 600 : 400,
                          fontSize: '0.875rem'
                        },
                        '& .MuiListItemText-secondary': {
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontSize: '0.75rem'
                        }
                      }}
                    />
                    {item.badge && (
                      <Badge 
                        badgeContent={item.badge} 
                        color="error"
                        sx={{
                          '& .MuiBadge-badge': {
                            fontSize: '0.7rem',
                            height: 18,
                            minWidth: 18
                          }
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* User Section */}
          <Box sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
                {userProfile.avatar}
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                  {userProfile.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  {userProfile.email}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Settings">
                <IconButton sx={{ 
                  borderRadius: 1, 
                  minWidth: 'auto',
                  p: 1,
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
                }}>
                  <SettingsIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Logout">
                <IconButton 
                  sx={{ 
                    borderRadius: 1, 
                    minWidth: 'auto',
                    p: 1,
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
                  }}
                  onClick={handleLogout}
                >
                  <LogoutIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Drawer>

        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              background: 'linear-gradient(180deg, #1e293b 0%, #334155 100%)',
              border: 'none'
            },
          }}
        >
          {/* Mobile drawer content - same as desktop */}
          <Box sx={{ 
            p: 3, 
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            background: 'rgba(255, 255, 255, 0.05)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ 
                  bgcolor: 'primary.main',
                  width: 40,
                  height: 40,
                  fontSize: '1.2rem',
                  fontWeight: 'bold'
                }}>
                  ET
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ 
                    color: 'white', 
                    fontWeight: 700,
                    fontSize: '1.1rem'
                  }}>
                    Estimation Tool
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={handleDrawerToggle} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          
          <List>
            {navItems.map((item) => (
              <ListItem key={item.key} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  selected={page === item.key}
                  onClick={() => handleNavigation(item.key)}
                  sx={{
                    mx: 1,
                    borderRadius: 2,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: page === item.key ? 'white' : 'rgba(255, 255, 255, 0.7)',
                    minWidth: 40
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.label} 
                    sx={{
                      '& .MuiListItemText-primary': {
                        color: page === item.key ? 'white' : 'rgba(255, 255, 255, 0.8)',
                        fontWeight: page === item.key ? 600 : 400,
                        fontSize: '0.875rem'
                      }
                    }}
                  />
                  {item.badge && (
                    <Badge 
                      badgeContent={item.badge} 
                      color="error"
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.7rem',
                          height: 18,
                          minWidth: 18
                        }
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Drawer>

        {/* Main Content */}
        <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Top App Bar */}
          <AppBar 
            position="static" 
            elevation={0}
            sx={{ 
              background: 'white',
              borderBottom: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}
          >
            <Toolbar sx={{ justifyContent: 'space-between', px: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: 2, display: { md: 'none' } }}
                >
                  <MenuIcon />
                </IconButton>
                
                <Box>
                  <Typography variant="h5" sx={{ 
                    color: 'text.primary',
                    fontWeight: 600,
                    fontSize: '1.25rem'
                  }}>
                    {currentPage?.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {currentPage?.description}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Search */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  bgcolor: '#f1f5f9',
                  borderRadius: 2,
                  px: 2,
                  py: 0.5,
                  minWidth: 200
                }}>
                  <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                  <InputBase
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    sx={{ flex: 1, fontSize: '0.875rem' }}
                  />
                </Box>

                {/* Online Status */}
                <Chip 
                  label={onlineStatus ? 'Online' : 'Offline'} 
                  color={onlineStatus ? 'success' : 'error'}
                  size="small"
                  variant="outlined"
                />

                {/* Theme Toggle */}
                <Tooltip title={`Switch to ${darkMode ? 'light' : 'dark'} mode`}>
                  <IconButton onClick={handleThemeToggle} color="primary">
                    {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
                  </IconButton>
                </Tooltip>

                {/* Notifications */}
                <Tooltip title="Notifications">
                  <IconButton onClick={handleNotificationClick} color="primary">
                    <Badge badgeContent={notifications.length} color="error">
                      <NotificationsIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>

                {/* User Menu */}
                <Tooltip title="User menu">
                  <IconButton onClick={handleUserMenuClick} color="primary">
                    <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                      {userProfile.avatar}
                    </Avatar>
                  </IconButton>
                </Tooltip>
              </Box>
            </Toolbar>
          </AppBar>

          {/* Page Content */}
          <Box sx={{ 
            flexGrow: 1, 
            p: 3,
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            minHeight: 'calc(100vh - 64px)'
          }}>
            <Container maxWidth="xl" sx={{ height: '100%' }}>
              {page === 'dashboard' && <Dashboard />}
              {page === 'proposals' && <Proposals />}
              {page === 'partners' && <Partners />}
              {page === 'featuresTasks' && <FeaturesTasks />}
              {page === 'documents' && <Documents />}
            </Container>
          </Box>
        </Box>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={handleCloseNotification}
          PaperProps={{
            sx: {
              width: 320,
              maxHeight: 400
            }
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Notifications
            </Typography>
          </Box>
          {notifications.length === 0 ? (
            <MenuItem>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No new notifications
              </Typography>
            </MenuItem>
          ) : (
            notifications.map((notification) => (
              <MenuItem key={notification.id} sx={{ py: 1.5 }}>
                <ListItemAvatar>
                  <Avatar sx={{ 
                    bgcolor: notification.type === 'error' ? 'error.main' :
                             notification.type === 'warning' ? 'warning.main' :
                             notification.type === 'success' ? 'success.main' : 'info.main',
                    width: 32,
                    height: 32
                  }}>
                    {notification.type.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {notification.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {notification.message} • {notification.time}
                  </Typography>
                </Box>
              </MenuItem>
            ))
          )}
        </Menu>

        {/* User Menu */}
        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={handleCloseUserMenu}
          PaperProps={{
            sx: {
              width: 280
            }
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}>
                {userProfile.avatar}
              </Avatar>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {userProfile.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {userProfile.role}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Last login: {userProfile.lastLogin}
                </Typography>
              </Box>
            </Box>
          </Box>
          <MenuItem onClick={handleCloseUserMenu}>
            <ListItemIcon>
              <AccountCircleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Profile</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleCloseUserMenu}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Settings</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>

        {/* Speed Dial */}
        <SpeedDial
          ariaLabel="SpeedDial"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          icon={<SpeedDialIcon />}
        >
          {speedDialActions.map((action) => (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={action.name}
              onClick={() => handleSpeedDialAction(action.action)}
            />
          ))}
        </SpeedDial>

        {/* Global Notifications */}
        <Snackbar
          open={showNotification}
          autoHideDuration={4000}
          onClose={() => setShowNotification(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={() => setShowNotification(false)} 
            severity={notificationSeverity} 
            sx={{ width: '100%' }}
          >
            {notificationMessage}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}

export default App; 